import { useCallback, useMemo } from "react";

import { FlagKeys } from "@saas/flags/utils";
// TODO: Refactor Cashier into `store`
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OrderRecipientInterface } from "@saas/order/utils";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ShopSettingsInterface } from "@saas/promotion/utils";
import { Money, MoneyData } from "@saas/shared/utils";

import {
  CashierOrderInterface,
  CashierSalesPersonInterface,
  CashierSessionInterface,
  CashierV2MachineContext,
  initialCashierV2Context,
} from "../../..";
import { StorePromoModel } from "../../../@machine/cashier/cashier-v2/types";
import { generateCashierSessionKey } from "../../../cashier/generate-cashier-session-key/generate-cashier-session-key";

import { useLocalStorage } from "usehooks-ts";

interface CashierMachineStorageContextCartOrder
  extends Omit<CashierOrderInterface, "createdAt"> {
  createdAt: string;
}

interface CashierMachineStorageContextCart {
  order: CashierMachineStorageContextCartOrder;
  totalOriginalAmount: {
    data: MoneyData;
  };
  totalDiscountAmount: {
    data: MoneyData;
  };
  balanceAmount?: {
    data: MoneyData;
  };
  totalProducts: number;
  totalVariants: number;
  promos: ReadonlyArray<StorePromoModel>;
  salesperson?: CashierSalesPersonInterface;
  recipients: ReadonlyArray<OrderRecipientInterface>;
  receiptKey?: string;
}

interface CashierMachineStorageContext
  extends Omit<CashierV2MachineContext, "cart" | "session" | "config"> {
  cart: CashierMachineStorageContextCart;
  session: Omit<CashierSessionInterface, "pettyCash"> & {
    pettyCash: {
      data: MoneyData;
    };
  };
  config: {
    online: boolean;
    flags?: Record<FlagKeys, boolean>;
    shopSettings?: ShopSettingsInterface;
  };
}

export type UseCashierMachineStorageContextReturnType = {
  context: CashierV2MachineContext;
  update: (context: CashierV2MachineContext) => void;
};

export const useCashierMachineStorageContext = (
  parentId: string
): UseCashierMachineStorageContextReturnType => {
  const [storageContext, setStorageContext] =
    useLocalStorage<CashierMachineStorageContext>(
      generateCashierSessionKey(parentId),
      {
        ...initialCashierV2Context,
        cart: {
          ...initialCashierV2Context.cart,
          order: {
            ...initialCashierV2Context.cart.order,
            createdAt:
              initialCashierV2Context.cart.order.createdAt.toISOString(),
          },
        },
      }
    );

  const context = useMemo<CashierV2MachineContext>(() => {
    return parseCashierContext.toMachine(storageContext);
  }, [storageContext]);

  const update = useCallback(
    (context: CashierV2MachineContext) => {
      setStorageContext(parseCashierContext.toStorage(context));
    },
    [setStorageContext]
  );

  return {
    context,
    update,
  };
};

export const parseCashierContext = {
  toStorage: (
    context: CashierV2MachineContext
  ): CashierMachineStorageContext => {
    return {
      cart: {
        order: {
          ...context.cart.order,
          createdAt: context.cart.order.createdAt.toISOString(),
        },
        totalDiscountAmount: {
          data: context.cart.totalDiscountAmount.data,
        },
        totalOriginalAmount: {
          data: context.cart.totalOriginalAmount.data,
        },
        balanceAmount: context.cart.balanceAmount
          ? {
              data: context.cart.balanceAmount.data,
            }
          : undefined,
        totalProducts: context.cart.totalProducts,
        totalVariants: context.cart.totalVariants,
        promos: context.cart.promos,
        salesperson: context.cart.salesperson,
        recipients: context.cart.recipients,
        receiptKey: context.cart.receiptKey,
      },
      session: {
        ...context.session,
        pettyCash: {
          data: context.session.pettyCash.data,
        },
      },
      config: {
        online: context.config.online,
        flags: context.config.flags,
        shopSettings: context.config.shopSettings,
      },
    };
  },
  toMachine: (
    storageContext: CashierMachineStorageContext
  ): CashierV2MachineContext => {
    return {
      cart: {
        order: {
          ...storageContext.cart.order,
          createdAt: new Date(storageContext.cart.order.createdAt),
        },
        totalDiscountAmount: new Money(
          storageContext.cart.totalDiscountAmount.data.integer
        ),
        totalOriginalAmount: new Money(
          storageContext.cart.totalOriginalAmount.data.integer
        ),
        balanceAmount: storageContext.cart.balanceAmount
          ? new Money(storageContext.cart.balanceAmount.data.integer)
          : undefined,
        totalProducts: storageContext.cart.totalProducts,
        totalVariants: storageContext.cart.totalVariants,
        promos: storageContext.cart.promos,
        salesperson: storageContext.cart.salesperson,
        recipients: storageContext.cart.recipients,
        receiptKey: storageContext.cart.receiptKey,
      },
      session: {
        ...storageContext.session,
        pettyCash: new Money(storageContext.session.pettyCash.data.integer),
      },
      config: {
        online: storageContext.config.online,
        flags: storageContext.config.flags,
        shopSettings: storageContext.config.shopSettings,
      },
    };
  },
};

export default useCashierMachineStorageContext;
